import React from "react";

import { FONT_WEIGHT_OPTIONS } from "@/features/react-editor/components/modifiers/constants";
import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import { MenuItem } from "@replo/design-system/components/menu/MenuItem";
import { ChevronDown } from "lucide-react";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";

interface FontWeightSelectorProps {
  fontWeight: number | null;
  onSelect: (fontWeight: number | null) => void;
  placeholder: string;
}

const FontWeightSelector: React.FC<FontWeightSelectorProps> = ({
  fontWeight,
  onSelect,
  placeholder,
}) => {
  const [selectedFontWeight, setSelectedFontWeight] = useOverridableState(
    fontWeight,
    onSelect,
  );

  const fontWeightOptions = FONT_WEIGHT_OPTIONS.map((weight) => ({
    value: weight.value.toString(),
    displayValue: weight.label,
    label: (
      <MenuItem
        variant="default"
        size="sm"
        selected={selectedFontWeight === weight.value}
      >
        <span style={{ fontWeight: weight.value }}>{weight.label}</span>
      </MenuItem>
    ),
  }));

  // Find current font weight option for display
  const currentOption = fontWeightOptions.find(
    (option) => option.value === selectedFontWeight?.toString(),
  );

  return (
    <div className="w-full overflow-hidden">
      <Combobox.Root
        options={fontWeightOptions}
        value={selectedFontWeight?.toString() ?? undefined}
        onChange={(value) => setSelectedFontWeight(Number(value))}
      >
        <Combobox.Trigger>
          <Combobox.SelectionButton
            layoutClassName="w-full"
            title={currentOption?.displayValue ?? placeholder}
            isPlaceholder={!selectedFontWeight}
            size="sm"
            endEnhancer={<ChevronDown size={12} className="text-muted" />}
            titleAlignment="start"
          />
        </Combobox.Trigger>
        <Combobox.Popover align="center" layoutClassName="w-[180px]">
          <Combobox.Content
            areOptionsSearchable={false}
            emptySearchMessage="No matches found"
          />
        </Combobox.Popover>
      </Combobox.Root>
    </div>
  );
};

export default FontWeightSelector;
